import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'

export type DashboardSidebarItem = {
  key: string
  label: ReactNode
  href?: string
  icon: ReactNode | ((props: { color: string }) => ReactNode)
}

export type DashboardSidebarProps = {
  items: DashboardSidebarItem[]
  activeKey?: string
  className?: string
}

const sidebarStyle = {
  width: designTokens.components.account.sidebar.width,
  paddingTop: designTokens.components.account.sidebar.paddingTop,
  paddingBottom: designTokens.components.account.sidebar.paddingTop,
  backgroundColor: designTokens.colors.background.white,
  borderRight: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminLight}`,
  boxShadow: designTokens.shadows.adminCard,
}

const brandStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.adminStrong,
  ...typographyStyle(designTokens.typography.account.brand),
  letterSpacing: designTokens.typography.account.brand.letterSpacing,
}

const navStyle = {
  gap: designTokens.spacing.md,
  paddingTop: designTokens.spacing.lg,
  paddingLeft: designTokens.spacing.lg,
  paddingRight: designTokens.spacing.lg,
}

const itemBaseStyle = {
  height: designTokens.components.account.sidebar.itemHeight,
  paddingTop: designTokens.components.account.sidebar.itemPaddingY,
  paddingBottom: designTokens.components.account.sidebar.itemPaddingY,
  gap: designTokens.components.account.sidebar.itemGap,
  ...typographyStyle(designTokens.typography.admin.menuItem),
  fontFamily: designTokens.typography.fontFamilies.primary,
  letterSpacing: designTokens.typography.admin.menuItem.letterSpacing,
}

const renderIcon = (icon: DashboardSidebarItem['icon'], color: string) => {
  if (typeof icon === 'function') {
    return icon({ color })
  }
  return icon
}

export function DashboardSidebar({ items, activeKey, className }: DashboardSidebarProps) {
  return (
    <aside className={cn('flex flex-col', className)} style={sidebarStyle}>
      <div className="flex w-full flex-col">
        <div
          className="flex h-full w-full items-center justify-center"
          style={{
            height: designTokens.components.account.sidebar.headerHeight,
            paddingLeft: designTokens.spacing.xxxl,
            paddingRight: designTokens.spacing.xxl,
            borderBottom: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminLight}`,
          }}
        >
          <span style={brandStyle}>epic growth</span>
        </div>
        <nav className="flex w-full flex-col" style={navStyle} aria-label="Dashboard navigation">
          {items.map((item) => {
            const isActive = item.key === activeKey
            const background = isActive
              ? designTokens.colors.neutral.grey
              : designTokens.colors.background.surface
            const color = isActive
              ? designTokens.colors.text.inverse
              : designTokens.colors.text.primary
            const radius = isActive
              ? designTokens.borders.radius.sm
              : designTokens.borders.radius.menu

            const content = (
              <div
                className="flex w-full flex-col items-center justify-center text-center"
                style={{
                  ...itemBaseStyle,
                  background,
                  color,
                  borderRadius: radius,
                }}
              >
                {renderIcon(item.icon, color)}
                <span className="whitespace-pre-wrap">{item.label}</span>
              </div>
            )

            if (item.href) {
              const isExternal = item.href.startsWith('http') || item.href.startsWith('mailto:')
              if (isExternal) {
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    className="w-full"
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {content}
                  </a>
                )
              }

              return (
                <Link
                  key={item.key}
                  to={item.href}
                  className="w-full"
                  aria-current={isActive ? 'page' : undefined}
                >
                  {content}
                </Link>
              )
            }

            return (
              <div key={item.key} className="w-full">
                {content}
              </div>
            )
          })}
        </nav>
      </div>
    </aside>
  )
}

DashboardSidebar.displayName = 'DashboardSidebar'
